/**
 * Low-level storage operations that work with IDBPObjectStore
 * These functions accept a store instance and can be used within transactions
 */

import type {
  TransactionRecord,
  GroupRecord,
  CategoryRecord,
  SyncMetadataInternal,
} from './storage.service';

// Use any for store type to avoid complex IDB type issues
type AnyStore = any;

// ============================================================================
// Transaction Store Operations
// ============================================================================

export function putTransactionInStore(
  store: AnyStore,
  record: TransactionRecord
): Promise<string> {
  return Promise.resolve(store.put(record));
}

export function getTransactionFromStore(
  store: AnyStore,
  id: string
): Promise<TransactionRecord | undefined> {
  return store.get(id);
}

export function deleteTransactionFromStore(
  store: AnyStore,
  id: string
): Promise<void> {
  return store.delete(id);
}

export function getAllTransactionsFromStore(
  store: AnyStore
): Promise<TransactionRecord[]> {
  return store.getAll();
}

export function getTransactionsByGroupFromStore(
  store: AnyStore,
  groupId: string
): Promise<TransactionRecord[]> {
  const index = store.index('by-group');
  return index.getAll(groupId);
}

// ============================================================================
// Group Store Operations
// ============================================================================

export function putGroupInStore(
  store: AnyStore,
  record: GroupRecord
): Promise<string> {
  return store.put(record);
}

export function getGroupFromStore(
  store: AnyStore,
  id: string
): Promise<GroupRecord | undefined> {
  return store.get(id);
}

export function getAllGroupsFromStore(
  store: AnyStore
): Promise<GroupRecord[]> {
  return store.getAll();
}

// ============================================================================
// Category Store Operations
// ============================================================================

export function putCategoryInStore(
  store: AnyStore,
  record: CategoryRecord
): Promise<string> {
  return store.put(record);
}

export function getCategoryFromStore(
  store: AnyStore,
  id: string
): Promise<CategoryRecord | undefined> {
  return store.get(id);
}

export function getCategoriesByGroupFromStore(
  store: AnyStore,
  groupId: string
): Promise<CategoryRecord[]> {
  const index = store.index('by-group');
  return index.getAll(groupId);
}

// ============================================================================
// Sync Metadata Store Operations
// ============================================================================

export function putSyncMetadataInStore(
  store: AnyStore,
  metadata: SyncMetadataInternal
): Promise<string> {
  return store.put(metadata);
}

export function getSyncMetadataFromStore(
  store: AnyStore,
  groupId: string
): Promise<SyncMetadataInternal | undefined> {
  return store.get(groupId);
}
